import Box from '@mui/material/Box';
import { Game } from 'functions/src/types/firestore/Game';
import { Tournament } from 'functions/src/types/firestore/Game/Tournament';
import { useEffect, useMemo, useState } from 'react';
import { SingleTournament } from '../../tournaments/SingleTournament';
import { firestore } from 'src/config/firebaseClient';
import {
  collection,
  DocumentSnapshot,
  onSnapshot,
  orderBy,
  query,
  Unsubscribe,
  doc,
} from 'firebase/firestore';
import { AccordionCompetitorProps } from 'src/components/tournaments/participants/accordion-competitor/AccordionCompetitor';
import { useAuth } from 'src/contexts/AuthContext';
import { Heat } from 'functions/src/types/firestore/Game/Tournament/Heat';
import { CompetitionAccordion } from 'src/components/leaderboards/CompetitionAcccordion';
import { useRouter } from 'next/router';
import Skeleton from '@mui/material/Skeleton';
import { GuestlistTeam } from 'functions/src/types/firestore/Game/Tournament/Guestlist';
import { WaitlistTeam } from 'functions/src/types/firestore/Game/Tournament/Waitlist';
import { ParticipantTeam } from 'functions/src/types/firestore/Game/Tournament/Participant';
import { teamsParser } from 'src/util/tournaments/teamParser';
import { BracketChunker } from 'functions/src/callable/tournament/cohort/BracketChunker';
import {
  Cohort,
  Bracket,
} from 'functions/src/types/firestore/Game/Tournament/Bracket';
import { convertToSingleValue } from 'src/util/convertToSingleValue';
import { ConverterFactory } from 'functions/src/util/firestore/ConverterFactory';

type TournamentsTabProps = Pick<Game, 'id'>;

type ExtendedTournament = {
  info: Tournament<Date>;
  registration: AccordionCompetitorProps[];
  waitlist: AccordionCompetitorProps[];
};

const participantsOnly = (phase: Tournament['phase']): boolean => {
  switch (phase) {
    case 'registration':
      return false;
    case 'checkIn':
      return false;
    case 'ready':
      return true;
    case 'live':
      return true;
    case 'finished':
      return true;
    default:
      return false;
  }
};

export const TournamentsTab = ({ id }: TournamentsTabProps) => {
  const router = useRouter();
  const { tournamentId: chosenTournamentId } = router.query;

  useEffect(() => {
    if (id) {
      const baseRef = `Game/${id}/Tournament`;

      onSnapshot(
        query(
          collection(firestore, baseRef).withConverter(
            ConverterFactory.buildDateConverter<Tournament<Date>>(),
          ),
          orderBy('date', 'desc'),
        ),
        async (tournamentSnapshot) => {
          setTournamentSnapshots(tournamentSnapshot.docs);
        },
      );
    }
  }, [id]);

  const { user } = useAuth();

  const heatSubscribers: {
    [key: string]: Unsubscribe;
  } = useMemo(() => {
    return {};
  }, []);

  const bracketSubscribers: {
    [key: string]: Unsubscribe;
  } = useMemo(() => {
    return {};
  }, []);

  const [tournamentSnapshots, setTournamentSnapshots] = useState<
    DocumentSnapshot<Tournament<Date>>[]
  >([]);
  useEffect(() => {
    tournamentSnapshots.forEach((tournamentSnapshot) => {
      if (
        !heatSubscribers[tournamentSnapshot.id] &&
        tournamentSnapshot.get('bracketType') === 'heats'
      ) {
        heatSubscribers[tournamentSnapshot.id] = onSnapshot(
          query(
            collection(
              firestore,
              `${tournamentSnapshot.ref.path}/Heat`,
            ).withConverter(ConverterFactory.buildDateConverter<Heat<Date>>()),
            // orderBy('startTime', 'asc'),
          ),
          (heatSnapshot) => {
            setHeatsByTournament((prev) => {
              return {
                ...prev,
                [tournamentSnapshot.id]: heatSnapshot.docs.map((doc) => {
                  return doc.data();
                }),
              };
            });
          },
        );
      }
    });
  }, [heatSubscribers, tournamentSnapshots]);

  const [heatsByTournament, setHeatsByTournament] = useState<{
    [key: string]: Heat<Date>[];
  }>({});

  const [cohortsByTournament, setCohortsByTournament] = useState<{
    [key: string]: Cohort<Date>[];
  }>({});
  useEffect(() => {
    tournamentSnapshots.forEach(async (tournamentSnapshot) => {
      const tournament = tournamentSnapshot.data();
      if (!tournament) {
        return;
      }
      const {
        guestlistAggregated = [],
        waitlistAggregated = [],
        participantsAggregated = [],
        phase,
      } = tournament;

      bracketSubscribers[tournamentSnapshot.id] = onSnapshot(
        doc(
          firestore,
          `${tournamentSnapshot.ref.path}/Bracket/${tournamentSnapshot.id}`,
        ).withConverter(ConverterFactory.buildDateConverter<Bracket<Date>>()),
        async (bracketSnap) => {
          const bracket = bracketSnap.exists() && bracketSnap.data().bracket;
          const onlyParticipants = participantsOnly(phase);
          const teams = onlyParticipants
            ? participantsAggregated
            : [...guestlistAggregated, ...waitlistAggregated];
          const relevantTeams = teams.filter((team) => {
            return team.members.find((member) => {
              return member.userId === user?.uid;
            });
          });

          if (bracket && bracket?.length) {
            const bracketLoser =
              bracketSnap.exists() && bracketSnap.data().bracketLoser;
            const bracketGrandFinal =
              bracketSnap.exists() && bracketSnap.data().grandFinal;
            const bracketChunker = new BracketChunker();
            const cohorts = bracketChunker.chunkIntoCohorts(bracket);
            const bracketChunkerLoser = new BracketChunker(
              undefined,
              undefined,
              {
                bracketLoser: true,
                grandFinal: false,
              },
            );
            const cohortsLoser = !!bracketLoser
              ? bracketChunkerLoser.chunkIntoCohorts(bracketLoser)
              : undefined;
            const bracketChunkerGrandFinal = new BracketChunker(
              undefined,
              undefined,
              {
                bracketLoser: false,
                grandFinal: true,
              },
            );
            const cohortGrandFinal = !!bracketGrandFinal
              ? bracketChunkerGrandFinal.chunkIntoCohorts(bracketGrandFinal)
              : undefined;

            const payoutSplit = Math.min(
              tournament.payouts?.length || 0,
              (cohorts[cohorts.length - 1].rounds?.length || 0) + 1,
            );
            const finalPayouts = tournament.payouts?.slice(
              !!bracketLoser ? 1 : 0,
              payoutSplit + (!!bracketLoser ? 1 : 0),
            );
            const qualifierPayouts =
              tournament.payouts?.slice(
                payoutSplit,
                tournament.payouts.length,
              ) || [];
            const cohortsPrized = [
              ...cohorts
                .filter((cohort) => {
                  return cohort.title !== 'Final';
                })
                .map((cohort) => {
                  return {
                    ...cohort,
                    prizes: qualifierPayouts,
                  };
                }),
              ...cohorts
                .filter((cohort) => {
                  return cohort.title === 'Final';
                })
                .map((cohort) => {
                  return {
                    ...cohort,
                    prizes: finalPayouts,
                  };
                }),
            ];

            const cohortGrandFinalPrized = cohortGrandFinal
              ? [
                  {
                    ...cohortGrandFinal[0],
                    prizes: tournament.payouts
                      ? [tournament.payouts[0], tournament.payouts[1]]
                      : [],
                  },
                ]
              : undefined;
            setCohortsByTournament((prev) => {
              return {
                ...prev,
                [tournamentSnapshot.id]: cohortsLoser
                  ? {
                      winner: cohortsPrized,
                      loser: cohortsLoser,
                      grandFinal: cohortGrandFinalPrized || undefined,
                    }
                  : cohortsPrized,
              };
            });
          }

          setTournamentsExtended((prev) => {
            return {
              ...prev,
              [tournamentSnapshot.id]: {
                info: tournament,
                registration: (onlyParticipants
                  ? participantsAggregated
                  : guestlistAggregated
                ).map((team) => {
                  return teamsParser(team, tournament.maxTeamSize);
                }),
                waitlist: onlyParticipants
                  ? []
                  : waitlistAggregated.map((team) => {
                      return teamsParser(team, tournament.maxTeamSize);
                    }),
                relevantTeams,
              },
            };
          });
        },
      );
    });
  }, [bracketSubscribers, heatsByTournament, tournamentSnapshots, user?.uid]);

  useEffect(() => {
    return () => {
      Object.values(heatSubscribers).forEach((subscriber) => {
        return subscriber();
      });
      Object.values(bracketSubscribers).forEach((subscriber) => {
        return subscriber();
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [tournamentsExtended, setTournamentsExtended] = useState<{
    [key: string]: ExtendedTournament & {
      relevantTeams: (
        | GuestlistTeam<Date>
        | WaitlistTeam<Date>
        | ParticipantTeam<Date>
      )[];
    };
  }>({});
  const competitions =
    Object.entries(tournamentsExtended).map(
      ([key, { info, registration, waitlist, relevantTeams }]) => {
        return {
          id: info.id,
          title: info.title,
          date: info.date,
          endDate: info.endDate,
          readyTime: info.readyTime,
          prize: info.prize,
          phase: info.phase,
          Details: (
            <SingleTournament
              tournament={info}
              registration={registration}
              waitlist={waitlist}
              teams={relevantTeams}
              gameId={id}
              heats={
                info.bracketType === 'heats'
                  ? heatsByTournament?.[`${key}`]
                  : cohortsByTournament?.[`${key}`]
              }
            />
          ),
        };
      },
    ) || [];
  return (
    <Box sx={{ mt: 4, px: 6 }}>
      {competitions?.length === tournamentSnapshots?.length ? (
        <CompetitionAccordion
          competitions={competitions}
          chosenTournamentId={convertToSingleValue(chosenTournamentId)}
        />
      ) : (
        <Skeleton width={'100%'} height={500} />
      )}
    </Box>
  );
};
